import axios, { AxiosError, AxiosInstance } from "axios";
import axiosRetry, { exponentialDelay } from "axios-retry";
import { LogsClient } from "./logs";
import { AuthClient } from "./auth";
import { UserClient } from "./user";

const createAxiosInstance = (path = "", authClient?: AuthClient) => {
  const axiosInstance = axios.create({
    baseURL: `${window.location.origin}/${path}`,
  });

  const retryCondition = (err: AxiosError) => {
    const toRetryCodes = [429, 500, 502, 503, 504];
    return toRetryCodes.includes(err.response?.status || 0);
  };

  const onRetry = (retryCount: number, error: AxiosError<never>) => {
    console.error(`Retrying request n. ${retryCount} time(s)`, error);
  };

  axiosRetry(axiosInstance, { retries: 6, retryCondition, retryDelay: exponentialDelay, onRetry });

  const apiKey = localStorage.getItem("apiKey");

  if (apiKey) {
    axiosInstance.defaults.headers = { ...axiosInstance.defaults.headers, "x-api-key": apiKey };
    document.cookie = "";
    return axiosInstance;
  }

  if (!authClient) {
    return axiosInstance;
  }

  axiosInstance.interceptors.request.use(async(config) => {
    await authClient.refresh(false);
    return config;
  });

  axiosInstance.interceptors.response.use(undefined, async(error) => {
    const originalRequest = error.config;

    // TODO: at a certain point we may want to handle the 401 using axios-retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      await authClient.refresh(true);

      return axiosInstance(originalRequest);
    }

    return Promise.reject(error);
  });

  return axiosInstance;
};

const simpleApi = createAxiosInstance();
const dbApi = createAxiosInstance("api/db/v2");
const logsClient = new LogsClient();
const userClient = new UserClient(dbApi);
const authClient = new AuthClient(userClient);

const API = {
  authClient,
  logsClient,
  axiosCancelToken: axios.CancelToken,
  createInstance: (path = ""): AxiosInstance => createAxiosInstance(path, authClient),
};

export default API;

export {
  simpleApi
};
